import { useCallback, useEffect, useMemo, useState } from "react";

import { showSuccessToast } from "../../utils/common";
import { CustomTableNavbar, ITableFilterProps, ITableNavbarProps } from "./CustomTableNavbar";

type TRow = { id: string; [key: string]: any };

interface IProps extends ITableNavbarProps {
  data: TRow[];
  headers: string[];
  className?: string;
  selected?: string[];
  multiSelect?: boolean;
  filtering?: ITableFilterProps;
  onBulkSelect?: () => void;
  onSelect?: (id: string) => void;
  onRowEdit?: (id: string) => void;
}

export const CustomTable = (props: IProps) => {
  const [finalData, setFinalData] = useState<TRow[]>([]);
  const { headers, data, multiSelect, selected, className, filtering, onSelect, onRowEdit, onBulkSelect, lastPage } =
    props;

  useEffect(() => {
    setFinalData(data);
  }, [data]);

  const convertHeaders = useCallback((header: string): string => {
    return header
      .split(/(?=[A-Z])/)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ")
      .toUpperCase();
  }, []);

  const handleOnSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value: inputValue } = e.target;

      const searchedData = data.filter((row: TRow) => {
        const values = Object.values(row);

        return values.some((value) =>
          value ? value.toString().toLowerCase().includes(inputValue.toLowerCase()) : false,
        );
      });

      setFinalData(searchedData);
    },
    [data],
  );

  const handleOnCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    showSuccessToast("Copied to clipboard!");
  };

  const convertedHeaders = useMemo(() => headers.map(convertHeaders), [headers, convertHeaders]);

  return (
    <div className="table-container d-flex flex-column mb-4">
      <CustomTableNavbar onChange={handleOnSearchChange} lastPage={lastPage} filtering={filtering} />

      <div className="table-responsive">
        <table className={`table table-hover table-bordered ${className ?? ""}`}>
          <thead className="text-dark">
            <tr className="text-center w-100">
              {multiSelect && onSelect && (
                <th>
                  <button className="btn btn-outline-success m-0 py-0 px-1 shadow-none" onClick={onBulkSelect}>
                    <i className="fa fa-check"></i>
                  </button>
                </th>
              )}
              {convertedHeaders.map((header, index) => (
                <th key={index} className="text-center">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-center w-100">
            {finalData.map((model, _) => (
              <tr key={model.id} className="w-100">
                {multiSelect && onSelect && (
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-box"
                      onChange={() => onSelect(model.id)}
                      checked={selected?.includes(model.id)}
                    />
                  </td>
                )}
                {Object.values(model)
                  .slice(0, headers.length)
                  .map((value, index) => {
                    const isLong = value?.toString().length > 150;
                    const formattedValue = isLong ? value.slice(0, 150) + "..." : value;

                    return (
                      <td key={index}>
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="col" style={{ width: "max-content", minWidth: "120px" }}>
                            {formattedValue ?? "-"}
                          </div>{" "}
                          {isLong && (
                            <i
                              className="col-1 fa-regular fa-clone text-success cursor-pointer"
                              onClick={() => handleOnCopy(value)}
                            ></i>
                          )}
                        </div>
                      </td>
                    );
                  })}
                {onRowEdit && (
                  <td onClick={() => onRowEdit(model.id)}>
                    <i className="fa fa-edit text-success cursor-pointer"></i>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
